import {
    TFormalOffer,
    TFormalOffers
} from '@helpers/FormalOffers/formalOffers.types';
import { getDurationInMonths } from '@helpers/GetDurationInMonths/getDurationInMonths';
import { fetchGetDrawDownRepaymentScheduleCreditFacility } from '@iwoca/lapi-client/edge';
import { useQueries } from '@tanstack/react-query';

import { useStateKey } from '../../../../../../iwQueryClient/useStateKey';
import { isSelectedAmountValid } from '../../../../../ui/ChooseAmountInputWithSlider/isSelectedAmountValid';
import { getNonInterestOnlyPaymentAmount } from '../../../../MyOffers.helpers';
import { DrawdownRepaymentSchedule } from '../../../../MyOffers.types';

type TOfferDurationType = 'shortest' | 'longest' | 'middle';

const getMonthlyRepayment = (data: DrawdownRepaymentSchedule) => {
    return data.summary.is_equal_repayment
        ? (data.summary.simplified_schedule?.equal_repayments?.amount ?? 0)
        : getNonInterestOnlyPaymentAmount(data.schedule);
};

export const useGetOfferButtons = ({
    formalOffers,
    drawdownAmount
}: {
    formalOffers: TFormalOffers;
    drawdownAmount: number;
}) => {
    const stateKey = useStateKey();

    const scheduleQueries = useQueries({
        queries: formalOffers.map((offer) => ({
            queryKey: [
                'drawdownRepaymentSchedule',
                offer.offer_id,
                stateKey,
                drawdownAmount,
                undefined
            ],
            queryFn: () =>
                fetchGetDrawDownRepaymentScheduleCreditFacility({
                    stateKey,
                    offerId: offer.offer_id!,
                    query: { amount: drawdownAmount }
                }),
            enabled: isSelectedAmountValid(drawdownAmount, offer),
            keepPreviousData: true
        }))
    });

    const repaymentByOfferId = new Map<string, number>();
    formalOffers.forEach((offer, index) => {
        const scheduleData = scheduleQueries[index].data?.data;
        if (scheduleData) {
            repaymentByOfferId.set(
                offer.offer_id!,
                getMonthlyRepayment(scheduleData)
            );
        }
    });

    const sortedOffers = [...formalOffers].sort((a, b) => {
        const aMonthlyRepayment = repaymentByOfferId.get(a.offer_id!);
        const bMonthlyRepayment = repaymentByOfferId.get(b.offer_id!);

        if (
            aMonthlyRepayment !== undefined &&
            bMonthlyRepayment !== undefined
        ) {
            return aMonthlyRepayment - bMonthlyRepayment;
        }

        return (
            getDurationInMonths(a.duration!) - getDurationInMonths(b.duration!)
        );
    });

    const getDurationType = (offer: TFormalOffer): TOfferDurationType => {
        const durations = formalOffers.map((o) =>
            getDurationInMonths(o.duration!)
        );
        const offerDuration = getDurationInMonths(offer.duration!);

        if (offerDuration === Math.min(...durations)) return 'shortest';
        if (offerDuration === Math.max(...durations)) return 'longest';
        return 'middle';
    };

    return {
        sortedOffers,
        getDurationType,
        repaymentByOfferId
    };
};
